import React, { useState, useEffect, useCallback } from 'react';
import { getAppHealth, getDbHealth } from '../../services/api';

// Base UI Components
import Card from '../ui/Card/Card';
import Spinner from '../ui/Spinner/Spinner';
import Badge from '../ui/Badge/Badge';
import Typography from '../ui/Typography/Typography';
import Stack, { HStack } from '../ui/Stack/Stack';
import Alert from '../ui/Alert/Alert';
import Box from '../ui/Box/Box'; // <<< ADDED IMPORT

// Local styles
import styles from './HealthChecker.module.css';

function HealthChecker({ showTitle = true, isEmbedded = false }) {
  const [appHealth, setAppHealth] = useState(null);
  const [dbHealth, setDbHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchHealthData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setAppHealth(null);
    setDbHealth(null);
    try {
      const [appData, dbData] = await Promise.all([
        getAppHealth(),
        getDbHealth(),
      ]);
      setAppHealth(appData);
      setDbHealth(dbData);
    } catch (err) {
      console.error("HealthChecker: Failed to fetch health data:", err);
      const errMessage = err.message || (err.response ? JSON.stringify(err.response.data) : 'Failed to connect or fetch health data.');
      setFetchError(errMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthData();
  }, [fetchHealthData]);

  const getStatusVariant = (statusString) => {
    if (!statusString) return 'default';
    switch (statusString.toLowerCase()) {
      case 'ok':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'warning';
    }
  };

  const renderStatus = (healthData, serviceName) => {
    let statusText = 'UNAVAILABLE';
    let statusVariant = 'warning';

    if (healthData && healthData.status) {
      statusText = healthData.status.toUpperCase();
      statusVariant = getStatusVariant(healthData.status);
    } else if (fetchError && !healthData) {
        statusText = 'ERROR';
        statusVariant = 'error';
    }
    
    return <Badge variant={statusVariant} size="md">{statusText}</Badge>;
  };

  const healthContent = (
    <Stack spacing="md">
      {loading ? (
        <Stack direction="row" spacing="md" alignItems="center" justifyContent="center" py="lg">
          <Spinner size="md" />
          <Typography color="secondary">Loading system health...</Typography>
        </Stack>
      ) : fetchError ? (
        <Alert variant="error" title="Health Check Failed">
          Could not retrieve system health status: {fetchError}
        </Alert>
      ) : (
        <>
          <HStack justifyContent="space-between" alignItems="center" className={styles.healthItemOverride}>
            <Typography fontWeight="medium">Application API Service:</Typography>
            <Box textAlign="right"> {/* This is where Box was used */}
                {renderStatus(appHealth, "Application API")}
                {appHealth?.message && <Typography variant="caption" color="secondary" display="block" mt="xs">- {appHealth.message}</Typography>}
            </Box>
          </HStack>

          <HStack justifyContent="space-between" alignItems="center" className={styles.healthItemOverride}>
            <Typography fontWeight="medium">Database Connection:</Typography>
            <Box textAlign="right"> {/* This is where Box was used */}
                {renderStatus(dbHealth, "Database")}
                {dbHealth?.message && <Typography variant="caption" color="secondary" display="block" mt="xs">- {dbHealth.message}</Typography>}
            </Box>
          </HStack>

          {!fetchError && appHealth?.status?.toLowerCase() === 'ok' && dbHealth?.status?.toLowerCase() === 'ok' && (
            <Alert variant="success" title="System Status" mt="md" className={styles.overallStatusAlert}>
              All core backend services appear operational.
            </Alert>
          )}
          {!fetchError && (appHealth?.status?.toLowerCase() !== 'ok' || dbHealth?.status?.toLowerCase() !== 'ok') && !loading && (
             <Alert variant="warning" title="System Status" mt="md" className={styles.overallStatusAlert}>
              One or more core backend services may have issues. Check details above or contact an administrator.
            </Alert>
          )}
        </>
      )}
    </Stack>
  );

  if (isEmbedded) {
    return healthContent;
  }

  return (
    <Card 
        title={showTitle ? "System Health Status" : undefined} 
        className={styles.healthCheckerWidgetOverride}
    >
      {healthContent}
    </Card>
  );
}

export default HealthChecker;